import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Divider, Empty, Modal, Progress, Tooltip } from 'antd';
import QRCode from 'qrcode.react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import colorsDark from '../../assets/colors/dark';
import Spinner from '../../components/Spinner';
import * as SERVICE from '../../constants/service.types';
import {
  VIEW_STREAM_SERVICE_EXTEND,
  ZOOM_STATE,
} from '../../constants/system.keys';
import { socketEmitEvent } from '../../redux/actions';
import AddCameraComponent from '../Camera/AddCamera';
import AddRemoteDevice from './AddRemoteDevice';
import { GpsItem } from './GpsItem';
import { SpeakerItem } from './SpeakerItem';
import MilestoneVideoStreamComponent from '../../components/VideoStream/MilestoneVideoStream';
import { CAMERA_TYPE } from '../../constants';
import { beeXLogger } from '../../helpers/logger';
import ViewStreamComponent from '../../components/VideoStream/v2';

const { confirm } = Modal;

const ExpandData = ({
  expands,
  addable,
  progressColors,
  isInternal,
  record,
  namespace,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [listCamera, setListCamera] = useState();
  const [listSpeaker, setListSpeaker] = useState();
  const [listGPS, setListGPS] = useState();

  const [visibleAddCamera, setVisibleAddCamera] = useState(false);
  const [visibleAddRemote, setVisibleAddRemote] = useState(false);
  const [key, setKey] = useState(Math.random());

  useEffect(() => {
    if (
      expands &&
      expands.cameras.length > 0 &&
      !(listCamera?.length === expands?.cameras?.length)
    ) {
      setListCamera(expands.cameras);
    }
  }, [expands, listCamera]);

  useEffect(() => {
    if (expands && expands.speakerSips.length > 0) {
      setListSpeaker(expands.speakerSips);
    }
  }, [expands, listSpeaker]);

  useEffect(() => {
    if (expands && expands.gpsInfos.length > 0) {
      setListGPS(expands.gpsInfos);
    }
  }, [expands, listSpeaker]);

  const handleOnAddCameraDone = useCallback(() => {
    const { deviceType, targetId, serial } = record;
    let data = {
      deviceType,
      targetId,
      serial,
      namespace,
    };

    let dataObj = {
      appServiceType: SERVICE.GET_DEVICE_INFO_EXTRA,
      requestId: new Date().getTime(),
      body: JSON.stringify(data),
    };

    return dispatch(socketEmitEvent(dataObj));
  }, [dispatch, namespace, record]);

  const handleOnAddCamera = useCallback(() => {
    // if (!record || (listCamera && record.maxAvailable - listCamera.length < 1))
    //   return Modal.error({
    //     title: t('This gateway is full'),
    //     content: t('Please contact the vendor for assistance!'),
    //   });

    if (record.state === 0)
      return Modal.warn({
        title: t('This gateway is offline'),
        content: t('Please contact the vendor for assistance!'),
      });

    return setVisibleAddCamera(true);
  }, [listCamera, record, t]);

  const compare = (a, b) => {
    if (a.resolution > b.resolution) {
      return -1;
    }
    if (a.resolution < b.resolution) {
      return 1;
    }
    return 0;
  };

  const onCheckZoomState = (
    dataArr,
    zoomState,
    zoomStateDefault,
    resolutionDefault,
  ) => {
    if (zoomState >= 0) {
      if (dataArr?.find((item) => item.zoomState === zoomState)) {
        return {
          dataZoomStates: [...dataArr],
          zoomState,
          resolution: dataArr?.find((item) => item.zoomState === zoomState)
            ?.resolution,
        };
      } else {
        return onCheckZoomState(
          dataArr,
          zoomState - 1,
          zoomStateDefault,
          resolutionDefault,
        );
      }
    } else {
      return {
        dataZoomStates: [...dataArr],
        zoomState: zoomStateDefault,
        resolution: resolutionDefault,
      };
    }
  };

  const onZoomState = (camera) => {
    let dataZoomState =
      (camera?.subSources &&
        JSON.parse(camera?.subSources)?.map((item) => ({
          resolution: item.resolution,
          zoomState: item.zoomState,
        }))) ??
      [];
    if (
      !dataZoomState?.find((item) => item.resolution === camera?.resolution)
    ) {
      dataZoomState = [
        ...dataZoomState,
        {
          resolution: camera?.resolution,
          zoomState: camera?.zoomState,
        },
      ];
    }
    const dataArr = dataZoomState
      ?.filter((item) => item.resolution >= 0 && item.zoomState >= 0)
      ?.sort(compare);
    const zoomState = ZOOM_STATE.THREE;
    const dataTemp = onCheckZoomState(
      dataArr,
      zoomState,
      camera?.zoomState,
      camera?.resolution,
    );
    return dataTemp;
  };

  const onCheckExternalDevice = useCallback(
    (serial) => {
      if (serial && serial.length > 0) {
        let data = {
          serial: serial.trim(),
          namespace,
          deviceType: 'sonoff',
          extraInfo: {
            factorySerial: serial.trim(),
          },
        };

        let dataObj = {
          appServiceType: SERVICE.CHECK_EXTERNAL_DEVICE,
          requestId: new Date().getTime(),
          body: JSON.stringify(data),
        };
        return dispatch(socketEmitEvent(dataObj));
      }
    },
    [dispatch, namespace],
  );

  const onAddRemote = useCallback(
    ({ serial, deviceSerial, name, wifiUuid, wifiPassword }) => {
      if (serial && serial.length > 0 && name && name.length > 0) {
        let data = {
          namespace,
          serial: deviceSerial,
          deviceType: 'sonoff',
          extraInfo: {
            name: name.trim(),
            factorySerial: serial,
            wifiUuid: wifiUuid ?? '',
            wifiPassword: wifiPassword ?? '',
            gatewaySerial: record?.serial,
          },
        };

        let dataObj = {
          appServiceType: SERVICE.REQUEST_ADD_EXTERNAL_DEVICE,
          requestId: new Date().getTime(),
          body: JSON.stringify(data),
        };
        return dispatch(socketEmitEvent(dataObj));
      }
    },
    [dispatch, namespace, record],
  );

  const onUpdateRemote = useCallback(
    (data, requestId) => {
      if (data) {
        let dataObj = {
          appServiceType: SERVICE.UPDATE_DEVICE,
          requestId,
          body: JSON.stringify({
            ...data,
            namespace,
          }),
        };

        return dispatch(socketEmitEvent(dataObj));
      }
    },
    [dispatch, namespace],
  );

  const handleRemoveDevice = useCallback(
    (name, serial) => {
      let data = {
        serial,
        namespace,
      };
      let dataObj = {
        appServiceType: SERVICE.REMOVE_DEVICE,
        requestId: new Date().getTime(),
        body: JSON.stringify(data),
      };
      confirm({
        title: t('Remove this device') + ` "${name}"?`,
        icon: <ExclamationCircleOutlined />,
        okText: t('Remove'),
        onOk() {
          dispatch(socketEmitEvent(dataObj));
          return handleOnAddCameraDone();
        },
        onCancel() {},
      });
    },
    [namespace, t, dispatch, handleOnAddCameraDone],
  );

  const requestGetSipAccount = () => {
    let data = {
      namespace,
    };
    console.log("🚀 ~ REQUEST_MAKE_CALL_OUT ~ body:", data)
    let dataObj = {
      appServiceType: SERVICE.REQUEST_MAKE_CALL_OUT,
      requestId: new Date().getTime(),
      body: JSON.stringify(data),
    };
    return dispatch(socketEmitEvent(dataObj));
  };

  useEffect(() => {
    if (namespace) {
      requestGetSipAccount();
    }
  }, [namespace]);

  return (
    <div>
      <Modal
        visible={visibleAddCamera}
        onCancel={() => {
          setVisibleAddCamera(false);
          setKey(Math.random());
        }}
        footer={null}
      >
        {visibleAddCamera && (
          <AddCameraComponent
            key={key}
            gatewaySerial={record && record.serial}
            gatewayAddress={{
              note: record?.note ?? '',
              lat: record?.lat ?? 0,
              lng: record?.lng,
            }}
            gatewayLocalAddress={record?.localAddress}
            onDone={(isMultipleAdd = false) => {
              handleOnAddCameraDone();
              beeXLogger.debug(
                '🚀 ~ ExpandData ~ isMultipleAdd:',
                isMultipleAdd,
              );
              if (!isMultipleAdd) {
                setVisibleAddCamera(false);
                setKey(Math.random());
              }
            }}
          />
        )}
      </Modal>
      <AddRemoteDevice
        isOpen={visibleAddRemote}
        onClose={() => setVisibleAddRemote(false)}
        namespace={namespace}
        gatewaySerial={record?.serial}
        onAddRemote={onAddRemote}
        onCheckExternalDevice={onCheckExternalDevice}
        onUpdateRemote={onUpdateRemote}
        handleOnAddCameraDone={handleOnAddCameraDone}
      />

      {expands ? (
        Object.keys(expands).length === 0 ? (
          <Empty />
        ) : (
          <div className="expand-data-component">
            <div className="data-side">
              <div className="description-expand-data">
                <Divider orientation="left">{t('Details')}</Divider>
                <div className="list-progress">
                  <div className="progress">
                    <Progress
                      type="circle"
                      percent={expands.cpu}
                      strokeColor={progressColors(expands.cpu)}
                      format={(percent) => {
                        return `${percent}%`;
                      }}
                    />
                    <div className="title">CPU</div>
                  </div>
                  <div className="progress">
                    <Progress
                      type="circle"
                      percent={expands.ram}
                      strokeColor={progressColors(expands.ram)}
                      format={(percent) => {
                        return `${percent}%`;
                      }}
                    />
                    <div className="title">RAM</div>
                  </div>
                  <div className="progress disk">
                    <Progress
                      type="circle"
                      percent={Math.floor(
                        (expands.disk / expands.totalDisk) * 100,
                      )}
                      strokeColor={progressColors(
                        Math.floor((expands.disk / expands.totalDisk) * 100),
                      )}
                      format={() => {
                        return `${expands.disk}/${expands.totalDisk} GB`;
                      }}
                    />
                    <div className="title">{t('Disk')}</div>
                  </div>
                  <div className="progress">
                    <Progress
                      type="circle"
                      percent={99}
                      strokeColor={colorsDark.$online}
                      format={() =>
                        listCamera && listCamera.length ? listCamera.length : 0
                      }
                    />
                    <div className="title">{t('Camera')}</div>
                  </div>
                </div>
              </div>
              <div className="description-expand-data">
                <Divider orientation="left">{t('Remote')}</Divider>
                <div className="list-progress-remote">
                  <div className="add-remote">
                    <Tooltip title={t('Add remote')}>
                      <div
                        className="add-button"
                        onClick={() => setVisibleAddRemote(true)}
                      >
                        <PlusOutlined style={{ fontSize: 60 }} />
                      </div>
                    </Tooltip>
                  </div>
                </div>
              </div>
              <div className="description-device-of-gateway-data">
                <Divider orientation="left">{t('Devices')}</Divider>
                <div className="list-video">
                  {listCamera &&
                    listCamera.map((camera) => (
                      <div key={camera.serial} className="child-video">
                        {camera?.typeCamera === CAMERA_TYPE.MILESTONE ? (
                          <MilestoneVideoStreamComponent
                            key={camera.serial}
                            zoomState={onZoomState?.(camera)}
                            name={camera.name}
                            typeCamera={camera.typeCamera}
                            resolution={camera.resolution}
                            serviceId={VIEW_STREAM_SERVICE_EXTEND.normal}
                            state={camera.state}
                            serial={camera.serial}
                            gatewaySerial={camera.gatewaySerial}
                            thumbnail={camera.thumbnail}
                            isInternal={isInternal}
                            permission={camera.permission}
                            isAllowStreaming={camera.isAllowStreaming}
                          />
                        ) : (
                          <ViewStreamComponent
                            name={camera.name}
                            typeCamera={camera.typeCamera}
                            resolution={camera.resolution}
                            zoomState={onZoomState(camera)}
                            serviceId={VIEW_STREAM_SERVICE_EXTEND.normal}
                            state={camera.state}
                            serial={camera.serial}
                            gatewaySerial={camera.gatewaySerial}
                            isInternal={isInternal}
                            thumbnail={camera.thumbnail}
                            permission={camera.permission}
                            isAllowStreaming={camera.isAllowStreaming}
                          />
                        )}
                      </div>
                    ))}
                  {addable && (
                    <div className="child-video add-camera">
                      <Tooltip title={t('Add camera')}>
                        <div
                          className="add-button"
                          onClick={() => handleOnAddCamera()}
                        >
                          <PlusOutlined style={{ fontSize: 60 }} />
                        </div>
                      </Tooltip>
                    </div>
                  )}
                </div>
              </div>

              {listSpeaker && listSpeaker.length > 0 && (
                <div className="description-device-of-gateway-data">
                  <Divider orientation="left">{t('Speakers')}</Divider>
                  <div className="list-video">
                    {listSpeaker &&
                      listSpeaker.map((speaker) => (
                        <SpeakerItem
                          key={speaker.serial}
                          speaker={speaker}
                          onUpdate={onUpdateRemote}
                          gatewaySerial={record?.serial}
                          handleUpdateSuccessful={handleOnAddCameraDone}
                        />
                      ))}
                  </div>
                </div>
              )}

              {listGPS && listGPS.length > 0 && (
                <div className="description-device-of-gateway-data">
                  <Divider orientation="left">{t('GPS')}</Divider>
                  <div className="list-video">
                    {listGPS &&
                      listGPS.map((gps) => (
                        <GpsItem
                          key={gps.serial}
                          gps={gps}
                          onUpdate={onUpdateRemote}
                          onRemove={handleRemoveDevice}
                          gatewaySerial={record?.serial}
                          handleUpdateSuccessful={handleOnAddCameraDone}
                        />
                      ))}
                  </div>
                </div>
              )}
            </div>
            <div className="qrcode-side">
              <div className="qrcode">
                <QRCode
                  renderAs="svg"
                  width={220}
                  height={220}
                  value={expands.serial}
                />
              </div>
              <div className="serial">{`Serial: ${expands.serial}`}</div>
            </div>
          </div>
        )
      ) : (
        <Spinner />
      )}
    </div>
  );
};

export default ExpandData;
